import { Fragment } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Divider, Drawer, IconButton, Link, Stack, Typography, useTheme } from '@mui/material'
import { ArrowUpRightIcon, LogOutIcon, PlayIcon, XIcon } from 'lucide-react'

import { Skeleton } from '@repo/ui'
import { chartSeriesColor } from 'components/analytics/chartStyle'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useReferrerState } from 'hooks'
import { useWebsiteJourney } from 'hooks/api'
import { ROUTES } from 'routes'
import type { DayRange, WebsiteSession } from 'services'
import { vhMinus } from 'utils/textZoom'

import type { TrackedLinkModalTarget } from '../../TrackedLinks/TrackedLinkModal'
import { AUDIENCE_LABELS, formatDuration, replayView } from '../websiteAnalyticsModel'
import { journeyTotals, sessionSourceLabel, summariseSession, type JourneyStep } from '../visitorJourneyModel'
import { dateTime, SectionNote, SourceNotices, ToneChip } from './shared'
import { CountryLabel, TrackedLinkLabel } from './visitorCells'

const DRAWER_WIDTH = 560

/** Who the drawer is about: the PostHog person when there is one, else the one session clicked. */
export type JourneyTarget = {
  personId: string | null
  sessionId: string
  email: string | null
  identified: boolean
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body3" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
    {children}
  </Typography>
)

/** One page of a session: the dot on the rail, the path, when, the time bar and the time on it. */
const Step = ({ step, color, last }: { step: JourneyStep; color: string; last: boolean }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '16px minmax(0, 1fr) 76px', columnGap: 3 }} data-testid="journey-step">
    {/* The rail: a dot per page, joined by a line to the next one. The time column is a fixed
        width so every step's bar has the same track and the bars compare across steps. */}
    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: 10, height: 10, mt: '5px', borderRadius: '50%', bgcolor: step.isExit ? 'transparent' : color, border: `2px solid ${color}`, zIndex: 1 }} />
      {!last ? <Box sx={{ position: 'absolute', top: 15, bottom: -4, width: 2, bgcolor: 'divider' }} /> : null}
    </Box>
    <Stack spacing={1} sx={{ minWidth: 0, pb: last ? 0 : 4 }}>
      <Stack direction="row" spacing={2} alignItems="baseline" sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere', minWidth: 0 }}>
          {step.path ?? '(no path)'}
        </Typography>
        {step.isEntry ? <ToneChip tone="info" label="Landed" /> : null}
        {step.isExit && !step.isEntry ? <ToneChip tone="neutral" label={step.onItNow ? 'On it now' : 'Exit'} /> : null}
      </Stack>
      <Typography variant="body3" color="text.secondary">
        {dateTime(step.at)}
      </Typography>
      {/* The time bar: share of the session's longest timed page. An unknown time has no bar. */}
      {step.seconds !== null ? (
        <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }} aria-hidden>
          <Box sx={{ width: `${Math.max(4, step.share * 100)}%`, height: '100%', bgcolor: color }} />
        </Box>
      ) : null}
    </Stack>
    <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {step.seconds !== null ? (
        formatDuration(step.seconds)
      ) : step.onItNow ? (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          on it now
        </Box>
      ) : (
        <InfoTooltip term="website-time-on-page">
          <Box component="span" sx={{ color: 'text.secondary', cursor: 'help' }} tabIndex={0}>
            unknown
          </Box>
        </InfoTooltip>
      )}
    </Typography>
  </Box>
)

const SessionBlock = ({
  session,
  index,
  count,
  color,
  onOpenLink,
}: {
  session: WebsiteSession
  index: number
  count: number
  color: string
  onOpenLink: (t: TrackedLinkModalTarget) => void
}) => {
  const sum = summariseSession(session)
  const replay = replayView(session.recording, Boolean(session.replayUrl))
  return (
    <Stack spacing={4} data-testid="journey-session">
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={3}>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {count > 1 ? `Visit ${count - index} of ${count}` : 'Visit'} · {dateTime(session.startedAt)}
          </Typography>
          <Typography variant="body3" color="text.secondary">
            {session.device ?? 'Unknown device'} · {sum.steps.length || session.pageviews} page
            {(sum.steps.length || session.pageviews) === 1 ? '' : 's'} ·{' '}
            {sum.knownSeconds !== null ? `${formatDuration(sum.knownSeconds)}${sum.unknownPages ? '+' : ''}` : 'time unknown'}
          </Typography>
        </Stack>
        {/* Replay once per visit: a step-level link would need PostHog's player to seek by
            timestamp, which its share links do not promise. */}
        {replay.show ? (
          <Link
            href={session.replayUrl ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            data-testid="journey-replay"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, typography: 'body3', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            <PlayIcon size={14} /> Replay{replay.label ? ` (${replay.label.toLowerCase()})` : ''}
          </Link>
        ) : replay.label ? (
          <InfoTooltip term={replay.term} text={replay.tooltip}>
            <Typography component="span" variant="body3" color="text.disabled" sx={{ cursor: 'help', whiteSpace: 'nowrap' }} tabIndex={0}>
              {replay.label}
            </Typography>
          </InfoTooltip>
        ) : null}
      </Stack>

      {/* Where the visit came from, above the path it took. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 4, rowGap: 2, alignItems: 'center' }}>
        <Label>Came from</Label>
        <Typography variant="body2" noWrap>
          {sessionSourceLabel(session)}
        </Typography>
        <Label>Tracked link</Label>
        <Box sx={{ minHeight: 20, minWidth: 0 }}>
          <TrackedLinkLabel link={session.trackedLink} code={session.inviteCode} onOpen={onOpenLink} />
        </Box>
      </Box>

      {sum.pagesUnavailable ? (
        <SectionNote>Pages could not be read from PostHog for this visit.</SectionNote>
      ) : sum.steps.length === 0 ? (
        <SectionNote>No pageviews recorded in this visit.</SectionNote>
      ) : (
        <Box aria-label="Pages in this visit, in order">
          {sum.steps.map((step, i) => (
            <Step key={`${step.at}-${i}`} step={step} color={color} last={i === sum.steps.length - 1} />
          ))}
        </Box>
      )}

      <Stack direction="row" spacing={2} alignItems="center">
        <LogOutIcon size={14} strokeWidth={1} />
        <Typography variant="body3" color="text.secondary">
          {sum.steps.some((s) => s.onItNow) ? 'Still on' : 'Left from'} <strong>{sum.exitPath ?? 'an unknown page'}</strong>
          {sum.knownSeconds !== null
            ? ` after ${formatDuration(sum.knownSeconds)}${sum.unknownPages ? ` or more (${sum.unknownPages} page${sum.unknownPages === 1 ? '' : 's'} not timed)` : ''}`
            : ''}
          .
        </Typography>
      </Stack>
    </Stack>
  )
}

/**
 * VRG-712 — one visitor's journey (Spencer, 2026-10-16: "how long, and their site-map journey:
 * a flow chart with how long they spent on each page"). A drawer from Visitor history.
 *
 * Every visit of the PostHog person in the page's range, newest first (max 20), each a vertical
 * flow of page → page with the time on each (`$prev_pageview_duration`, paired by pageview id —
 * unknown stays "unknown", never 0), where they came from and the tracked link above, the exit
 * page and the visit's total below. Only visitors with analytics on exist here at all: PostHog
 * records nothing for anyone else, and pages viewed before a visitor accepted are not recorded.
 */
export const WebsiteJourneyDrawer = ({
  target,
  range,
  rangeLabel,
  onClose,
  onOpenLink,
  countryNames,
}: {
  target: JourneyTarget | null
  range: DayRange
  rangeLabel: string
  onClose: () => void
  onOpenLink: (t: TrackedLinkModalTarget) => void
  countryNames: ReadonlyMap<string, string>
}) => {
  const theme = useTheme()
  const color = chartSeriesColor(theme)
  const referrer = useReferrerState('Analytics')
  const { data, isLoading, error } = useWebsiteJourney(
    target ? { ...range, ...(target.personId ? { personId: target.personId } : { sessionId: target.sessionId }) } : null,
  )
  const sessions = data?.sessions ?? []
  const totals = journeyTotals(sessions)
  const first = sessions[0]
  const email = first?.email ?? target?.email ?? null

  return (
    <Drawer
      anchor="right"
      open={!!target}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          margin: 4,
          borderRadius: 3,
          maxHeight: vhMinus(32),
          width: DRAWER_WIDTH,
          maxWidth: 'calc(100vw - 32px)',
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Stack sx={{ p: 6, pb: 4, flexShrink: 0 }} spacing={2} data-testid="journey-drawer">
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="body1" noWrap sx={{ minWidth: 0 }}>
              Visitor journey
            </Typography>
            <InfoTooltip term="website-visitor-journey" />
          </Stack>
          <IconButton onClick={onClose} aria-label="Close" sx={{ color: theme.palette.text.primary }}>
            <XIcon strokeWidth={1} />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {email ? (
            <Link component={RouterLink} to={ROUTES.INTERNAL_ACCESS} state={referrer} underline="hover" noWrap sx={{ typography: 'body2' }}>
              {email}
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {target?.identified ? 'Identified visitor' : 'Anonymous visitor'}
            </Typography>
          )}
          {first?.personUrl ? (
            <InfoTooltip text="Open the PostHog person">
              <Link href={first.personUrl} target="_blank" rel="noopener noreferrer" aria-label="Open the PostHog person" sx={{ display: 'inline-flex' }}>
                <ArrowUpRightIcon size={14} />
              </Link>
            </InfoTooltip>
          ) : null}
        </Stack>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {AUDIENCE_LABELS.consented} only: PostHog records nothing for visitors whose analytics are off, and pages viewed
          before a visitor accepted are not recorded. Visits in {rangeLabel}, newest first
          {target?.personId ? '' : ' (this visit only: no PostHog person to group by)'}.
        </Typography>
      </Stack>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 6 }}>
        {error ? (
          <SectionNote>
            {(error as { status?: number }).status === 403
              ? 'You cannot view visitor journeys.'
              : 'Could not load this journey. Close the panel and try again.'}
          </SectionNote>
        ) : isLoading || !data ? (
          // Mirrors the summary row and two visits of steps (§19).
          <Stack spacing={6}>
            <Skeleton height={18} width="70%" />
            {[0, 1].map((i) => (
              <Stack key={i} spacing={3}>
                <Skeleton height={16} width="50%" />
                <Skeleton height={14} lines={4} />
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack spacing={6}>
            <SourceNotices sources={[['PostHog', data.posthog]]} />
            {sessions.length === 0 ? (
              <SectionNote>No analytics-on visits by this visitor in this range.</SectionNote>
            ) : (
              <>
                {/* The whole visitor in one line: visits, pages, time across them, where. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4 }} data-testid="journey-summary">
                  <Stack spacing={0.5}>
                    <Label>Visits</Label>
                    <Typography variant="body1">
                      {sessions.length}
                      {data.capped ? '+' : ''}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.5}>
                    <Label>Pages</Label>
                    <Typography variant="body1">{totals.pages}</Typography>
                  </Stack>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Label>Total time</Label>
                      <InfoTooltip term="website-time-on-page" />
                    </Stack>
                    <Typography variant="body1">
                      {totals.knownSeconds !== null ? `${formatDuration(totals.knownSeconds)}${totals.unknownPages ? '+' : ''}` : '—'}
                    </Typography>
                  </Stack>
                </Box>
                <Box sx={{ height: 40 }}>
                  <CountryLabel code={first?.country ?? null} city={first?.city ?? null} names={countryNames} />
                </Box>
                {data.capped ? <SectionNote>Showing the 20 most recent visits.</SectionNote> : null}
                <Stack spacing={6} divider={<Divider />}>
                  {sessions.map((s, i) => (
                    <Fragment key={s.sessionId}>
                      <SessionBlock session={s} index={i} count={sessions.length} color={color} onOpenLink={onOpenLink} />
                    </Fragment>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Drawer>
  )
}
